import { useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../../stores/useEditorStore';
import { TOOLS_CONFIG } from '../../constants/tools';
import { computePlaneFromLine } from '../../lib/slicing';
import { getPlaneMeshContour, closedPolylineLength } from '../../lib/planeContour';

// How far the pointer may travel between down and up and still count as a click
// rather than an orbit drag.
const CLICK_SLOP_PX = 4;

/**
 * Circumference tool: two clicks on the model define a plane; compute mesh-plane
 * contour. If exactly one closed loop, show circumference and draw LineLoop; else show error.
 * Orbit controls remain enabled.
 */
const CircumferenceHandler = () => {
  const activeTool = useEditorStore((s) => s.activeTool);
  const [
    sceneRefs,
    setCircumference,
    setCircumferenceError,
    setCircumferenceContourLine,
  ] = useEditorStore(
    useShallow((state) => [
      state.sceneRefs,
      state.setCircumference,
      state.setCircumferenceError,
      state.setCircumferenceContourLine,
    ])
  );

  const circPointsRef = useRef([]);
  const sphereRefsRef = useRef([null, null]);
  const pointerStartRef = useRef(null);

  const isCircumferenceActive = activeTool === TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id;

  const handleClick = useCallback(
    (e) => {
      if (!isCircumferenceActive || !sceneRefs?.shadedMesh || !sceneRefs?.scene || !sceneRefs?.camera || !sceneRefs?.renderer) return;

      const { camera, renderer, shadedMesh, scene } = sceneRefs;
      const rect = renderer.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const ray = new THREE.Raycaster();
      ray.setFromCamera(mouse, camera);
      const hits = ray.intersectObject(shadedMesh, false);
      if (!hits.length) return;

      const pt = hits[0].point.clone();

      if (circPointsRef.current.length === 0) {
        setCircumference(null);
        setCircumferenceError(null);
        const existing = useEditorStore.getState().circumferenceContourLine;
        if (existing && sceneRefs.scene) {
          sceneRefs.scene.remove(existing);
          existing.geometry?.dispose();
          existing.material?.dispose();
        }
        setCircumferenceContourLine(null);
        sphereRefsRef.current.forEach((m) => m && (m.visible = false));
      }

      circPointsRef.current = [...circPointsRef.current, pt];

      let sphere = sphereRefsRef.current[circPointsRef.current.length - 1];
      if (!sphere) {
        const geo = new THREE.SphereGeometry(0.3, 16, 16);
        const mat = new THREE.MeshBasicMaterial({ color: 0x583ca3, depthTest: false });
        sphere = new THREE.Mesh(geo, mat);
        sphere.renderOrder = 999;
        scene.add(sphere);
        sphereRefsRef.current[circPointsRef.current.length - 1] = sphere;
      }
      sphere.position.copy(pt);
      sphere.visible = true;

      if (circPointsRef.current.length === 2) {
        const [pointA, pointB] = circPointsRef.current;
        const plane = computePlaneFromLine(pointA, pointB, camera);
        const { closedPolylines, openPolylines } = getPlaneMeshContour(shadedMesh, plane);

        if (closedPolylines.length === 1 && openPolylines.length === 0) {
          const contour = closedPolylines[0];
          const lengthCm = closedPolylineLength(contour);
          setCircumference(lengthCm);
          setCircumferenceError(null);
          const geom = new THREE.BufferGeometry().setFromPoints(contour);
          const mat = new THREE.LineBasicMaterial({
            color: 0x583ca3,
            depthTest: false,
            linewidth: 2,
          });
          const line = new THREE.LineLoop(geom, mat);
          line.renderOrder = 999;
          scene.add(line);
          setCircumferenceContourLine(line);
        } else {
          setCircumference(null);
          setCircumferenceError('Selection did not form a closed loop.');
        }

        circPointsRef.current = [];
        sphereRefsRef.current.forEach((m) => m && (m.visible = false));
      }
    },
    [
      isCircumferenceActive,
      sceneRefs,
      setCircumference,
      setCircumferenceError,
      setCircumferenceContourLine,
    ]
  );

  // Place a point on a CLICK, not on a pointer-down. The tool shares the canvas
  // with OrbitControls, and a rotate starts with a pointer-down on the model:
  // firing on down meant every drag to look around dropped a measuring point
  // (and every second one ran a full contour sweep), which reads as the orbit
  // controls being broken.
  useEffect(() => {
    if (!isCircumferenceActive || !sceneRefs?.renderer) return undefined;
    const canvas = sceneRefs.renderer.domElement;

    const onPointerDown = (e) => {
      if (e.button !== 0) return;
      pointerStartRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    };
    const onPointerUp = (e) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start || start.id !== e.pointerId) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (dx * dx + dy * dy > CLICK_SLOP_PX * CLICK_SLOP_PX) return; // it was a drag
      handleClick(e);
    };
    const onPointerCancel = () => { pointerStartRef.current = null; };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      pointerStartRef.current = null;
    };
  }, [isCircumferenceActive, sceneRefs, handleClick]);

  // When switching away from Circumference (or unmounting), remove contour line and spheres from scene
  useEffect(() => {
    return () => {
      const { sceneRefs: refs, circumferenceContourLine: contourLine, setCircumferenceContourLine: setLine, setCircumference, setCircumferenceError } = useEditorStore.getState();
      if (refs?.scene && contourLine) {
        refs.scene.remove(contourLine);
        contourLine.geometry?.dispose();
        contourLine.material?.dispose();
      }
      setLine(null);
      setCircumference(null);
      setCircumferenceError(null);
      sphereRefsRef.current.forEach((m) => {
        if (m && m.parent) {
          m.parent.remove(m);
          m.geometry?.dispose();
          m.material?.dispose();
        }
      });
      sphereRefsRef.current = [null, null];
      circPointsRef.current = [];
    };
  }, []);

  return null;
};

export default CircumferenceHandler;
